import React, {
  ReactNode,
  useEffect,
  useState,
  MouseEvent,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import DropdownMenuArrow, {
  ARROW_SIZE,
  PopperPlacement,
} from 'core/components/DropdownMenu/arrow';
import useStorageState from 'core/hooks/storage';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

type Storage = 'local' | 'session';

type Props = {
  storageKey: string;
  anchorEl: HTMLElement | null;
  children: ReactNode;
  show?: boolean;
  storage?: Storage;
  placement?: PopperPlacement;
  autoHide?: boolean;
  autoHideDuration?: number;
  offset?: number;
  className?: string;
  icon?: ReactNode;
  elevated?: boolean;
  dismissOnAnchorClick?: boolean;
};

const defaultIcon = <LightbulbOutlinedIcon className="!h-5 !w-5 -mt-0.5 shrink-0" />;

export default function OneTimeAnchoredHint({
  storageKey,
  anchorEl,
  children,
  show = true,
  storage = 'local',
  placement = 'top-start',
  autoHide = false,
  autoHideDuration = 5000,
  offset = ARROW_SIZE,
  className,
  icon = defaultIcon,
  elevated = false,
  dismissOnAnchorClick = true,
}: Props) {
  const [dismissed, setDismissed] = useStorageState<boolean>(storageKey, false, storage);
  const [arrowElement, setArrowElement] = useState<HTMLSpanElement | null>(null);
  const open = show && !dismissed && !!anchorEl;

  useEffect(() => {
    if (!open || !autoHide || autoHideDuration <= 0) return () => {};

    const timeoutId = window.setTimeout(() => {
      setDismissed(true);
    }, autoHideDuration);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autoHide, autoHideDuration, open, setDismissed]);

  useEffect(() => {
    function dismissHint() {
      setDismissed(true);
    }

    if (dismissOnAnchorClick && !isNil(anchorEl)) {
      anchorEl.addEventListener('click', dismissHint);
    }

    return () => {
      anchorEl?.removeEventListener('click', dismissHint);
    };
  }, [anchorEl, dismissOnAnchorClick, setDismissed]);

  const dismiss = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setDismissed(true);
  };

  return open ? (
    <Popper
      open
      anchorEl={anchorEl}
      placement={placement}
      disablePortal={false}
      modifiers={[
        {
          name: 'offset',
          options: { offset: [0, ARROW_SIZE + offset] },
        },
        {
          name: 'arrow',
          enabled: true,
          options: { element: arrowElement },
        },
      ]}
      sx={{
        zIndex: (theme) => (elevated ? theme.zIndex.modal + 1 : theme.zIndex.drawer - 1),
      }}
    >
      <div className="relative">
        <DropdownMenuArrow
          placement={placement}
          arrowRef={setArrowElement}
          outerColor="#93c5fd"
          innerColor="#3b82f6"
        />
        <Paper
          className={clsx(
            'flex max-w-[320px] items-start gap-2 rounded-md border !border-blue-500',
            '!bg-blue-500 !px-3 !py-2 !text-white !text-xs !font-semibold !shadow-lg',
            className,
          )}
          elevation={0}
        >
          {icon && (
            <span className="mt-0.5 flex shrink-0 text-white">
              {icon}
            </span>
          )}
          <div className="min-w-0 flex-1 leading-5">
            {children}
          </div>
          <IconButton
            size="small"
            className="!-mr-2 !-mt-1 !p-1"
            onClick={dismiss}
            aria-label="Dismiss hint"
          >
            <CloseIcon className="!h-4 !w-4 text-white" />
          </IconButton>
        </Paper>
      </div>
    </Popper>
  ) : null;
}
